import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import MetaTags from 'components/meta-tags';
import { DUTCHIE_PRIVACY_URL, DUTCHIE_TERMS_URL } from 'shared/constants/legal-urls';
import { Button } from 'components/core';
import { RibbonStarV2 } from 'src/assets/ribbon-star-v2';
import getLocationsFromCoordinates from 'src/utils/get-locations-from-coordinates';
import type { LandingPageProps } from '../types';

const US_STATES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
};

type StateLegalTextConfig = {
  firstText: string;
  secondText: string | ((enterpriseName: string) => string);
};

const stateLegalText: Partial<Record<string, StateLegalTextConfig>> = {
  OH: {
    firstText:
      'Rewards and agree to receive marketing emails and SMS (msg & data rates may apply). Unsubscribe anytime by email link, preference center, or replying STOP to SMS. By continuing, you agree to our ',
    secondText: (enterpriseName: string) =>
      `. I understand that the data collected by ${enterpriseName} for the purposes of administering this loyalty program will not be transmitted to or stored by any state governments`,
  },
};
const defaultText =
  'Rewards and agree to receive marketing emails and SMS (msg & data rates may apply). Unsubscribe anytime by email link, preference center, or replying STOP to SMS. By continuing, you agree to our ';

export function LandingPage({ enterprise, onStartEarningRewards }: LandingPageProps): JSX.Element {
  const termsUrl = enterprise?.termsAndConditionsUrl ?? DUTCHIE_TERMS_URL;
  const privacyUrl = enterprise?.privacyPolicyUrl ?? DUTCHIE_PRIVACY_URL;
  const stateOptions = (enterprise?.dposLoyaltyIsoRegionCodes ?? [])
    .filter((code): code is string => code != null)
    .map((code) => {
      const stateCode = code.replace('US-', '');
      return {
        code: stateCode,
        name: US_STATES[stateCode] || stateCode,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // Auto-select if only one state available
  const initialState = stateOptions.length === 1 ? stateOptions[0].code : '';
  const [selectedState, setSelectedState] = useState<string>(initialState);
  const hasUserSelectedStateRef = useRef(false);

  // Silently auto-detect the visitor's state so the dropdown is pre-filled with their likely
  // location instead of forcing a manual search -- this is background detection, not a
  // user-initiated action, so failures/denials fall back quietly to the manual dropdown.
  useEffect(() => {
    // useEffect only runs client-side, so `navigator` is always defined here.
    if (stateOptions.length <= 1 || !navigator.geolocation) {
      return undefined;
    }

    let isCancelled = false;
    navigator.geolocation.getCurrentPosition((position) => {
      const { latitude, longitude } = position.coords;
      void getLocationsFromCoordinates({ latitude, longitude }).then(({ data, status }) => {
        if (isCancelled || status !== 200 || hasUserSelectedStateRef.current) {
          return;
        }
        const detectedState = data[0]?.state;
        const matchedOption = stateOptions.find((option) => option.code === detectedState);
        if (matchedOption) {
          setSelectedState(matchedOption.code);
        }
      });
    });
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount using the enterprise's (stable) state list
  }, []);

  const handleStartClick = (): void => {
    if (!selectedState) {
      return;
    }
    onStartEarningRewards(selectedState);
  };

  const buildLegalText = (): string => {
    const legalConfig = stateLegalText[selectedState];
    if (!legalConfig) {
      return '';
    }

    const enterpriseName = String(enterprise?.displayName ?? enterprise?.name ?? 'Company');
    const secondText =
      typeof legalConfig.secondText === 'function' ? legalConfig.secondText(enterpriseName) : legalConfig.secondText;

    return secondText;
  };

  return (
    <>
      <MetaTags title='Join Our Rewards Program' description='Earn exclusive benefits with every purchase' />

      <LandingContainer>
        <LandingCard>
          <IconContainer>
            <RibbonStarV2 />
          </IconContainer>

          <LandingHeader>
            <LandingTitle>Join {String(enterprise?.displayName ?? enterprise?.name ?? 'Company/Program')}</LandingTitle>
            <LandingSubtitle>
              {String(
                enterprise?.description ??
                  'Earn rewards by creating an account to unlock exclusive benefits with every purchase'
              )}
            </LandingSubtitle>
          </LandingHeader>

          {stateOptions.length > 0 && (
            <StateSelectionSection>
              <StateLabel htmlFor='state-select'>Please select your state:</StateLabel>
              <StateSelect
                id='state-select'
                value={selectedState}
                onChange={(e) => {
                  hasUserSelectedStateRef.current = true;
                  setSelectedState(e.target.value);
                }}
                onBlur={(e) => {
                  hasUserSelectedStateRef.current = true;
                  setSelectedState(e.target.value);
                }}
                aria-label='Select your state'
              >
                <option value='' disabled>
                  State
                </option>
                {stateOptions.map(({ code, name }) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </StateSelect>
            </StateSelectionSection>
          )}

          <StartButton onClick={handleStartClick} disabled={stateOptions.length > 0 && !selectedState}>
            START EARNING REWARDS
          </StartButton>

          <LegalText>
            By creating an account, you'll be enrolled in{' '}
            {String(enterprise?.displayName ?? enterprise?.name ?? 'Company')}{' '}
            {stateLegalText[selectedState]?.firstText ?? defaultText}
            <LegalLink href={termsUrl} target='_blank' rel='noopener noreferrer'>
              Terms
            </LegalLink>{' '}
            and{' '}
            <LegalLink href={privacyUrl} target='_blank' rel='noopener noreferrer'>
              Privacy Policy
            </LegalLink>
            {buildLegalText()}.
          </LegalText>
        </LandingCard>
      </LandingContainer>
    </>
  );
}

// Styled Components for Landing Page
const LandingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 1rem;
`;

const LandingCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 3rem 2rem;
  width: 100%;
  max-width: 500px;
  text-align: center;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;

  svg {
    width: 80px;
    height: 80px;
  }
`;

const LandingHeader = styled.div`
  margin-bottom: 2rem;
`;

const LandingTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1rem 0;
  line-height: 1.2;
`;

const LandingSubtitle = styled.p`
  font-size: 1.125rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
`;

const StateSelectionSection = styled.div`
  margin-bottom: 2rem;
  text-align: left;
`;

const StateLabel = styled.label`
  display: block;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
`;

const StateSelect = styled.select`
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  color: #1f2937;
  background-color: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236b7280' d='M1.41 0L6 4.59 10.59 0 12 1.41l-6 6-6-6z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  option {
    padding: 0.5rem;
  }

  option:disabled {
    color: #9ca3af;
  }
`;

const StartButton = styled(Button)<{ disabled?: boolean }>`
  width: 100%;
  background: ${({ disabled }) => (disabled ? '#e5e7eb' : 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)')};
  color: ${({ disabled }) => (disabled ? '#9ca3af' : 'white')};
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem 2rem;
  border-radius: 12px;
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  margin-bottom: 2rem;
  text-transform: var(--dct--customized-textTransforms-buttons, uppercase);
  letter-spacing: 0.5px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ disabled }) => (disabled ? '#e5e7eb' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)')};
    transform: ${({ disabled }) => (disabled ? 'none' : 'translateY(-2px)')};
    box-shadow: ${({ disabled }) => (disabled ? 'none' : '0 8px 20px rgba(59, 130, 246, 0.3)')};
  }

  &:active {
    transform: ${({ disabled }) => (disabled ? 'none' : 'translateY(0)')};
  }
`;

const LegalText = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
  margin: 0;
  text-align: left;
`;

const LegalLink = styled.a`
  color: var(--dct--customized-colors-buttonsLinks, #3b82f6);

  &:active,
  &:focus,
  &:hover {
    text-decoration: underline;
  }
`;
